import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useSiteLanguage } from '../context/SiteLanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrolmentSuccess() {
  const { t } = useSiteLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const enquiryId = params.get('enquiry_id');

  const [status, setStatus] = useState('verifying'); // verifying | success | error | no-stripe
  const [courseTitle, setCourseTitle] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  useEffect(() => {
    if (!sessionId) {
      // No session ID — Stripe wasn't configured; enrolment was saved directly
      setStatus('no-stripe');
      return;
    }

    const url = `${API}/payments/verify-session?session_id=${encodeURIComponent(sessionId)}${enquiryId ? `&enquiry_id=${encodeURIComponent(enquiryId)}` : ''}`;

    axios.get(url)
      .then(res => {
        setCourseTitle(res.data.course_title || '');
        setCustomerEmail(res.data.customer_email || '');
        setStatus('success');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [sessionId, enquiryId]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">{t('enrolSuccess.verifying', 'Verifying your payment…')}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <XCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
            {t('enrolSuccess.errorTitle', 'Payment verification failed')}
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            {t('enrolSuccess.errorDesc', "We could not confirm your payment. If you were charged, please contact us and we'll resolve it right away.")}
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            {t('enrolSuccess.contactUs', 'Contact Support')}
          </Link>
        </div>
      </div>
    );
  }

  // success or no-stripe (enquiry saved without payment)
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={32} className="text-green-600" />
        </div>

        {status === 'success' ? (
          <>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
              {t('enrolSuccess.title', 'Enrolment Confirmed!')}
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed mb-2">
              {courseTitle
                ? t('enrolSuccess.descCourse', `Your payment was successful and you are now enrolled on ${courseTitle}.`).replace('{courseTitle}', courseTitle)
                : t('enrolSuccess.desc', 'Your payment was successful and your enrolment has been confirmed.')}
            </p>
            {customerEmail && (
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                {t('enrolSuccess.emailSent', `A confirmation has been sent to ${customerEmail}.`).replace('{email}', customerEmail)}
              </p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
              {t('apply.applicationReceived', 'Application received')}
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              {t('apply.successFollowup', 'A member of our admissions team will contact you within one working day to verify your details and complete your enrolment.')}
            </p>
          </>
        )}

        <div className="flex gap-3 justify-center">
          <Link
            to="/courses"
            className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('apply.browseMoreCourses', 'Browse more courses')}
          </Link>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            {t('apply.returnHome', 'Return to home')}
          </Link>
        </div>
      </div>
    </div>
  );
}
